// ══════════════════════════════════════════════════════════════════
//  ED CRM - Cliente Supabase
//  Versão: 1.0 · Abril 2026
// ══════════════════════════════════════════════════════════════════
//  Baseado em: Config/03_auth_supabase.js
// ══════════════════════════════════════════════════════════════════

import {
  createClient,
  type SupabaseClient,
  type Session,
  type User,
  type RealtimeChannel,
  type RealtimePostgresChangesPayload,
} from '@supabase/supabase-js';
import { supabaseConfig } from '@config/index';
import type { Database } from '@/types/database.types';

export type TypedSupabaseClient = SupabaseClient<Database>;

type LeadRow = Database['public']['Tables']['leads']['Row'];
type LeadInsert = Database['public']['Tables']['leads']['Insert'];
type ProfileRow = Database['public']['Tables']['profiles']['Row'];

let _supabase: TypedSupabaseClient | null = null;

/**
 * Obtém ou cria instância do cliente Supabase (singleton)
 */
export const getSupabase = (): TypedSupabaseClient | null => {
  if (_supabase) return _supabase;

  const { url, anonKey } = supabaseConfig;

  if (!url || !anonKey) {
    console.warn('[Supabase] Credenciais não configuradas. Usando modo local.');
    return null;
  }

  try {
    _supabase = createClient<Database>(url, anonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        storage: window.localStorage,
      },
      realtime: {
        params: { eventsPerSecond: 10 }
      },
      db: {
        schema: 'public'
      }
    });

    return _supabase;
  } catch (error) {
    console.error('[Supabase] Erro ao inicializar cliente:', error);
    return null;
  }
};

/**
 * Verifica se o Supabase está configurado
 */
export const isSupabaseReady = (): boolean => {
  return !!(supabaseConfig.url && supabaseConfig.anonKey);
};

/**
 * Cliente admin (service_role). Usado para acoes que exigem privilegio
 * administrativo (auth.admin.createUser etc). RISCO DE SEGURANCA: a chave
 * fica embutida no bundle quando exposta via REACT_APP_*. Idealmente isso
 * deveria estar em uma Edge Function. Lazy: so cria quando solicitado.
 */
let _supabaseAdmin: TypedSupabaseClient | null = null;
export const getSupabaseAdmin = (): TypedSupabaseClient | null => {
  if (_supabaseAdmin) return _supabaseAdmin;
  const { url, serviceRoleKey } = supabaseConfig;
  if (!url || !serviceRoleKey) return null;
  try {
    _supabaseAdmin = createClient<Database>(url, serviceRoleKey, {
      auth: { autoRefreshToken: false, persistSession: false },
    });
    return _supabaseAdmin;
  } catch (error) {
    console.error('[Supabase] Erro ao inicializar cliente admin:', error);
    return null;
  }
};

/**
 * Obtém sessão atual do usuário
 */
export const getSession = async (): Promise<Session | null> => {
  const supabase = getSupabase();
  if (!supabase) return null;

  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    return session;
  } catch (error) {
    console.error('[Supabase] Erro ao obter sessão:', error);
    return null;
  }
};

/**
 * Obtém usuário atual
 */
export const getCurrentUser = async (): Promise<User | null> => {
  const supabase = getSupabase();
  if (!supabase) return null;

  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  } catch (error) {
    console.error('[Supabase] Erro ao obter usuário:', error);
    return null;
  }
};

/**
 * Realiza login com email e senha
 */
export const signIn = async (email: string, password: string) => {
  const supabase = getSupabase();
  if (!supabase) {
    throw new Error('Supabase não configurado');
  }

  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.toLowerCase().trim(),
      password,
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('[Supabase] Erro no login:', error);
    throw error;
  }
};

/**
 * Realiza logout
 */
export const signOut = async (): Promise<void> => {
  const supabase = getSupabase();
  if (!supabase) return;

  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  } catch (error) {
    console.error('[Supabase] Erro no logout:', error);
    throw error;
  }
};

/**
 * Busca perfil do usuário na tabela profiles
 */
export const getUserProfile = async (userId: string): Promise<ProfileRow | null> => {
  const supabase = getSupabase();
  if (!supabase) return null;

  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('[Supabase] Erro ao buscar perfil:', error);
    return null;
  }
};

export interface LeadQueryFilters {
  status?: Database['public']['Enums']['lead_status'];
  sdrId?: string;
  closerId?: string;
  dateFrom?: string;
  dateTo?: string;
}

/**
 * Busca leads do Supabase
 */
export const fetchLeads = async (filters: LeadQueryFilters = {}): Promise<LeadRow[]> => {
  const supabase = getSupabase();
  if (!supabase) return [];

  try {
    let query = supabase.from('leads').select('*');

    // Aplicar filtros
    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    if (filters.sdrId) {
      query = query.eq('sdr_id', filters.sdrId);
    }
    if (filters.closerId) {
      query = query.eq('closer_id', filters.closerId);
    }
    if (filters.dateFrom) {
      query = query.gte('created_at', filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte('created_at', filters.dateTo);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('[Supabase] Erro ao buscar leads:', error);
    return [];
  }
};

/**
 * Salva lead no Supabase
 */
export const saveLead = async (lead: LeadInsert): Promise<LeadRow | null> => {
  const supabase = getSupabase();
  if (!supabase) return null;

  try {
    const { data, error } = await supabase
      .from('leads')
      .upsert(lead, { onConflict: 'id' })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('[Supabase] Erro ao salvar lead:', error);
    throw error;
  }
};

/**
 * Deleta lead do Supabase
 */
export const deleteLead = async (leadId: string): Promise<boolean> => {
  const supabase = getSupabase();
  if (!supabase) return false;

  try {
    const { error } = await supabase
      .from('leads')
      .delete()
      .eq('id', leadId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('[Supabase] Erro ao deletar lead:', error);
    return false;
  }
};

/**
 * Inscreve-se em canal realtime para updates
 */
export const subscribeToTable = (
  table: string,
  callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void,
): RealtimeChannel | null => {
  const supabase = getSupabase();
  if (!supabase) return null;

  try {
    const channel = supabase
      .channel(`table-${table}-changes`)
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table,
        },
        (payload) => {
          callback(payload);
        }
      )
      .subscribe();

    return channel;
  } catch (error) {
    console.error('[Supabase] Erro ao inscrever no canal:', error);
    return null;
  }
};

/**
 * Cancela inscrição em canal realtime
 */
export const unsubscribeFromChannel = (channel: RealtimeChannel | null): void => {
  if (!channel) return;
  // Resolve o singleton vivo (getSupabase) em vez do snapshot module-level
  // `supabase`, que pode ter sido capturado como null no import (achado #6).
  const supabase = getSupabase();
  if (supabase) supabase.removeChannel(channel);
};

/**
 * Sincroniza estado local com Supabase
 */
export const syncWithSupabase = async (
  localState: { leads?: LeadInsert[] },
): Promise<boolean> => {
  const supabase = getSupabase();
  if (!supabase) return false;

  try {
    // Sincronizar leads
    if (localState.leads && localState.leads.length > 0) {
      for (const lead of localState.leads) {
        await saveLead(lead);
      }
    }

    return true;
  } catch (error) {
    console.error('[Supabase] Erro na sincronização:', error);
    return false;
  }
};

/**
 * Export direto da instância Supabase para compatibilidade
 */
export const supabase = getSupabase();
